import { ILayer } from '../layer.type';
import { environment } from '../../../../../environments/environment';
import { _ } from '../../../utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';
import { ValmetDnaValue } from '~app/custom/neve/valmet-dna.service';
import { sum } from 'lodash';

const markerUrlBase = environment.assetRoot + '/data/T14/markers/';

const smallElementBaseZoom = 13;
const smallElementValveZoom = 12;
const smallElementsOtherZoom = 11;

const valveLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': [
    'step', ['zoom'],
    0,
    15, 10,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.28,
    16, 0.46,
  ],
};

const valvePaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
};

const othersLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.45,
    16, 0.8,
  ],
};

const customerLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementBaseZoom, 0.1,
    16, 0.8,
  ],
};

const textLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-field': ['get', 'text'],
  'text-size': 14,
  'text-radial-offset': 1,
  'text-anchor': 'top', // overwritten by some layers
  'text-max-width': 100, // prevent wrapping
};

const textPaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
};

/**
 * Neve
 */
export type TagPair = [position: string, tag: string];

const defaultValueTransformer = (value: ValmetDnaValue): ValmetDnaTransformedValue => {
  return {
    ...value,
    numericValue: +value.value,
    formattedValue: value.value
  }
};

const configureTags = (entries: (TagPair | ValmetDnaTagConfig)[]): ValmetDnaTagConfig[] => {
  return entries.map(entry => {
    if(Array.isArray(entry)) {
      return {
        tagpair: entry,
        valueTransformer: defaultValueTransformer
      }
    };

    return entry;
  });
};

export type ValmetDnaTransformedValue = ValmetDnaValue & {
  numericValue: number
  formattedValue?: string
};

export type ValmetDnaTagConfig = {
  tagpair: TagPair,
  valueTransformer?: (value: ValmetDnaValue) => ValmetDnaTransformedValue
};

const negativeValueTransformer = (value): ValmetDnaTransformedValue => {
  return {
    ...value,
    numericValue: -1 * (+value.value)
  }
};

export interface NeveTenantConfig extends TenantConfig {
  tenantCustomData: {
    valmetDnaTags: {
      siloSurfaceInfo: ValmetDnaTagConfig[],
      pressureInfo: ValmetDnaTagConfig[],
      powerInfo: {
        mainNetwork: ValmetDnaTagConfig[],
        auxiliaryNetwork: ValmetDnaTagConfig[],
        allNetworks: ValmetDnaTagConfig[],
      },
      electricityInfo: ValmetDnaTagConfig[],
      outsideTemperatureInfo: ValmetDnaTagConfig[],
    }
  }
};

const getTenantCustomData = (): NeveTenantConfig['tenantCustomData'] => {
  const mainNetwork = configureTags([
    ['01NDB10U901', 'av'],
    ['NDB10CU010',  'av'],
    ['NDB31CU001',  'av'],
    ['NDE11FU002',  'av'], // KL-AKKU PURKU KL-TEHO
    {
      tagpair: ['NDE11FU001',  'av'], // KL-AKKU LATAUS KL-TEHO
      valueTransformer: negativeValueTransformer
    },
    ['3NPU01',      'av'],
    ['5NDB20FN001', 'av'],
    ['10NPU01',     'av'],
    ['11ND20CU901', 'av'],
    ['17N20CU001',  'av'],
    ['00100WQ01VP', 'av'],
    ['LAOU01',      'av'],
  ]);

  const auxiliaryNetwork = configureTags([
    ['30N10CU001',  'av'],
    ['20NDA30CU001','av'],
    ['31NDB22CU001','av']
  ]);

  return {
    valmetDnaTags: {
      siloSurfaceInfo: configureTags([
        ['END71CL001B', 'av'],
        ['ENE20CL005', 'av'],
      ]),
      pressureInfo: configureTags([
        ['NDB10CP901', 'av'],
        ['CYG26CP901', 'av'],
        ['RAB12CP902', 'av'],
        ['RAB12CP901', 'av'],
        ['CYG29CP901', 'av'],
        ['UM10PD01',   'av'],
        ['CYG25CP901', 'av'],
        ['CYG28CP901', 'av'],
        ['CYG30CP901', 'av'],
        ['16PD901',    'av'],
        ['00100PE00P', 'av'],
        ['17N10CP901', 'av'],
        ['06DP001',    'av'],
        ['UM10PD0401', 'av'],
        ['05000PE01P', 'av'],
        ['05100PE01P', 'av'],
      ]),
      powerInfo: {
        mainNetwork,
        auxiliaryNetwork,
        allNetworks: mainNetwork.concat(auxiliaryNetwork)
      },
      electricityInfo: configureTags([
        ['AQ011B67', 'av']
      ]),
      outsideTemperatureInfo: configureTags([
        ['1UN10T004', 'av']
      ]),
    }
  }
}

export default (): NeveTenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 66.523,
    lng: 25.751,
  },
  defaultZoom: 10.82,
  images: [
    { id: 'asiakas', url: markerUrlBase + 'asiakas-Asiakas.png' },
    { id: 'asiakas-iso', url: markerUrlBase + 'asiakas-Asiakas_iso.png' },
    { id: 'lampolaitos-bio-on', url: markerUrlBase + 'running-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-on', url: markerUrlBase + 'running-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-on', url: markerUrlBase + 'running-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-on', url: markerUrlBase + 'running-lämpölaitos.png' },
    { id: 'voimalaitos-on', url: markerUrlBase + 'running-Voimalaitos.png' },
    { id: 'lampolaitos-bio-off', url: markerUrlBase + 'gray-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-off', url: markerUrlBase + 'gray-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-off', url: markerUrlBase + 'gray-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-off', url: markerUrlBase + 'gray-lämpölaitos.png' },
    { id: 'voimalaitos-off', url: markerUrlBase + 'gray-Voimalaitos.png' },
    { id: 'lampolaitos-bio-loading', url: markerUrlBase + 'fault-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-loading', url: markerUrlBase + 'fault-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-loading', url: markerUrlBase + 'fault-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-loading', url: markerUrlBase + 'fault-lämpölaitos.png' },
    { id: 'voimalaitos-loading', url: markerUrlBase + 'fault-Voimalaitos.png' },
    { id: 'paineero-on', url: markerUrlBase + 'running-Paineeneromittauspiste.png' },
    { id: 'paineero-off', url: markerUrlBase + 'critical-Paineeneromittauspiste.png' },
    { id: 'paineero-loading', url: markerUrlBase + 'fault-Paineeneromittauspiste.png' },
    { id: 'pumppuasema-on', url: markerUrlBase + 'running-Pumppuasema.png' },
    { id: 'pumppuasema-off', url: markerUrlBase + 'critical-Pumppuasema.png' },
    { id: 'pumppuasema-loading', url: markerUrlBase + 'fault-Pumppuasema.png' },
  ],
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      },
      {
        name: 'B-kriittiset',
        value: 2,
        boolean: true
      },
      {
        name: 'C-kriittiset',
        value: 3,
        boolean: true
      }
    ]
  }],
  tenantCustomData: getTenantCustomData()
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T14.json by MapService.addLayer
// numbering for used layers refers to: https://miproagile.atlassian.net/browse/MR-789
export function getLayers(): ILayer[] {
  return [
    // 1. Kaukolämpöverkosto (Julkinen osa)
    {
      id: 'Putki_julkinen',
      vectorSourceLayer: 'Putki_julkinen',
      name: 'Kaukolämpöverkosto',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: [
          'interpolate', ['linear'], ['zoom'],
          0, 0.5,
          10, 1.25,
          22, 4
        ],
        visible: true,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'Putki_pieni',
      vectorSourceLayer: 'Putki_pieni',
      name: 'Kaukolämpöverkosto (pienet putket)',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: 1,
        visible: true,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 2. Ei-julkinen verkosto (Kaukolämpöverkoston osa, esim. Laji: DN150/2Mpuk_ei_julkinen)
    {
      id: 'Putki_ei_julkinen',
      vectorSourceLayer: 'Putki_ei_julkinen',
      name: 'Kaukolämpöverkosto (ei-julkinen)',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#fd0150',
        strokeWeight: 1,
        visible: false,
      },
      mapboxOptions: {
        paint: {
          'line-gap-width': 2,
          'line-dasharray': [1, 1],
          'line-blur': 1
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 3. Laitokset
    {
      id: 'Syottopiste',
      name: 'Laitokset',
      orderingWeight: 5,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.powerInfo.allNetworks.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => `${sum(measurements.map(({value}) => parseFloat(value))).toPrecision(2)} ${measurements[0].unit}`,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: ['concat',
            [
              'case',
              ['==', ['get', 'Laji'], 'Voimalaitos'], 'voimalaitos',
              ['in', 'BIO', ['get', 'Polttoaine']], 'lampolaitos-bio',
              ['in', 'Turve', ['get', 'Polttoaine']], 'lampolaitos-kpa',
              ['in', 'Hake', ['get', 'Polttoaine']], 'lampolaitos-kpa',
              ['in', 'POK', ['get', 'Polttoaine']], 'lampolaitos-oil',
              ['in', 'POR', ['get', 'Polttoaine']], 'lampolaitos-oil',
              'lampolaitos',
            ],
            '-',
            [
              'case',
              ['!', ['has', 'maxValue']], 'loading',
              ['>', ['get', 'maxValue'], 0.0], 'on',
              'off',
            ]
          ]
        },
        visible: true,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...textLayout,
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            smallElementValveZoom, 1,
            16, 1.2,
          ],
          'text-radial-offset': 1.5, // compensate for larger icon
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Aseman tai kaivon tunnus',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
          coordinates: false,
          hideProperties: ['text', 'maxValue'],
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // (siilot)
    {
      id: 'Siilot',
      name: 'Siilot',
      orderingWeight: 7,
      belowRoadLayers: false,
      selectable: false,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.siloSurfaceInfo.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => measurements.map(
        ({label, value, unit}) => `${label.replace(/\s+pinta 2$/i, '')}: ${Math.round(parseFloat(value))} ${unit}`
      ).join('\n'),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: false,
        icon: {
          url: markerUrlBase + 'running-Siilo.png',
        },
        visible: false,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...textLayout,
          'text-anchor': [
            'coalesce',
            ['get', 'text-anchor'],
            'left',
          ],
          'icon-size': 0.75,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 4. Pumppaamot
    {
      id: 'Pumppuasema',
      name: 'Pumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: [
            'concat',
            'pumppuasema',
            '-',
            [
              'case',
              ['!', ['has', 'maxValue']], 'loading',
              ['>=', ['get', 'maxValue'], 0.5], 'on',
              'off',
            ]
          ]
        },
        visible: true,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...othersLayout,
          ...textLayout,
          'text-anchor': 'right',
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
          hideProperties: ['text', 'maxValue'],
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 5. Kaivot
    // TODO
    // 6. Porasulkuventtiilit (Löytyy kasettiventtiilin alta)
    {
      id: 'Porasulkuventtiili',
      vectorSourceLayer: 'Porasulkuventtiili',
      name: 'Porasulkuventtiilit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'PS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 7. Kiertolenkit (Löytyy kasettiventtiilin alta)
    {
      id: 'Kiertolenkki',
      vectorSourceLayer: 'Kiertolenkki_sv',
      name: 'Kiertolenkit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'running-kiertolenkki.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...othersLayout,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 8. Elementtiventtiilit (alasvetovalikko)
    //    7 alaryhmää (Jäljelle jääneet osat kasettiventtiilin alta kun ottaa pois porasulkuventtiilit ja kiertolenkit).
    {
      id: 'Haaroitusventtiili_kaivossa',
      vectorSourceLayer: 'Haaroitusventtiili_kaivossa',
      name: 'Haaroitusventtiili kaivossa',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'HVK',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Kaivosulkuventtiili',
      vectorSourceLayer: 'Kaivosulkuventtiili',
      name: 'Kaivosulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'KS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Sulkuventtiili',
      vectorSourceLayer: 'Sulkuventtiili',
      name: 'Sulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'S',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_IV',
      vectorSourceLayer: 'SV_IV',
      name: 'SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_TV',
      vectorSourceLayer: 'SV_TV',
      name: 'SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'IV_SV_IV',
      vectorSourceLayer: 'IV_SV_IV',
      name: 'IV+SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'IV+SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'TV_SV_TV',
      vectorSourceLayer: 'TV_SV_TV',
      name: 'TV+SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'TV+SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 9. Liittymät
    {
      id: 'Liittyma',
      vectorSourceLayer: 'Liittyma',
      name: 'Liittymät',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          urlExpression: [
            'case',
            ['>=', ['number', ['get', 'Lämmitettävä rakennustilavuus (m³)']], 100_000], 'asiakas-iso',
            'asiakas',
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        layout: customerLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 10. Verkostopisteet
    // TODO
    // 11. Paine-eromittauspisteet (Löytyy Solmupisteen alta, muut solmupisteet ei tarvitse näkyä)
    {
      id: 'Paine_eromittaus',
      name: 'Paine-eromittauspisteet',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.pressureInfo.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => measurements.map(
        ({value, unit}) => `${parseFloat(value).toFixed(1)} ${unit}`
      ).join('\n'),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: [
            'concat',
            'paineero',
            '-',
            [
              'case',
              ['!', ['has', 'maxValue']], 'loading',
              ['>=', ['get', 'maxValue'], 0.5], 'on',
              'off',
            ]
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...othersLayout,
          ...textLayout,
          'text-anchor': 'bottom',
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
          hideProperties: ['text', 'maxValue'],
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 12. Osoitenumerot
    getSharedLayer('houseNumbers'),
    // 13. Pinnanmuodot
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    // 14. Vesialueet
    getSharedLayer('groundWaterAreas'), // 14.1 Pohjavesialueet
    getSharedLayer('drainageAreas'), // 14.2 Valuma-alueet
    getSharedLayer('basinOutlets'), // 14.3 Purkupisteet
  ];
}
